import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EmployeeCustomPermissionsPage = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [customPermissions, setCustomPermissions] = useState(null);
  const [employeePermissions, setEmployeePermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modals
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  
  // Form states
  const [selectedPermissionId, setSelectedPermissionId] = useState('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [hasExpiry, setHasExpiry] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const [employeesRes, permsRes] = await Promise.all([
        axios.get(`${API_URL}/api/hr/employees`, config),
        axios.get(`${API_URL}/api/rbac/permissions/by-category`, config)
      ]);

      setEmployees(employeesRes.data || []);
      setPermissions(permsRes.data || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const fetchEmployeeData = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const [customRes, allPermsRes] = await Promise.all([
        axios.get(`${API_URL}/api/rbac/employees/${employeeId}/custom-permissions`, config),
        axios.get(`${API_URL}/api/rbac/employees/${employeeId}/all-permissions`, config)
      ]);

      setCustomPermissions(customRes.data || null);
      setEmployeePermissions(allPermsRes.data || null);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      setCustomPermissions(null);
      setEmployeePermissions(null);
    }
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    fetchEmployeeData(employee.id);
  };

  const handleGrantPermission = async () => {
    if (!selectedEmployee || !selectedPermissionId || !reason) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      await axios.post(
        `${API_URL}/api/rbac/employees/${selectedEmployee.id}/custom-permissions`,
        {
          permission_id: selectedPermissionId,
          override_type: 'grant',
          reason: reason,
          expires_at: hasExpiry ? expiresAt : null
        },
        config
      );

      alert('تم منح الصلاحية بنجاح!');
      setShowGrantModal(false);
      resetForm();
      fetchEmployeeData(selectedEmployee.id);
      setSaving(false);
    } catch (error) {
      console.error('Error granting permission:', error);
      alert(error.response?.data?.detail || 'حدث خطأ أثناء منح الصلاحية');
      setSaving(false);
    }
  };

  const handleRevokePermission = async () => {
    if (!selectedEmployee || !selectedPermissionId || !reason) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      await axios.post(
        `${API_URL}/api/rbac/employees/${selectedEmployee.id}/custom-permissions`,
        {
          permission_id: selectedPermissionId,
          override_type: 'revoke',
          reason: reason
        },
        config
      );

      alert('تم إلغاء الصلاحية بنجاح!');
      setShowRevokeModal(false);
      resetForm();
      fetchEmployeeData(selectedEmployee.id);
      setSaving(false);
    } catch (error) {
      console.error('Error revoking permission:', error);
      alert(error.response?.data?.detail || 'حدث خطأ أثناء إلغاء الصلاحية');
      setSaving(false);
    }
  };

  const handleRemoveCustomPermission = async (permissionId) => {
    if (!selectedEmployee) return;
    
    if (!window.confirm('هل أنت متأكد من إزالة هذا التخصيص؟')) return;

    try {
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      await axios.delete(
        `${API_URL}/api/rbac/employees/${selectedEmployee.id}/custom-permissions/${permissionId}`,
        config
      );

      alert('تم إزالة التخصيص بنجاح!');
      fetchEmployeeData(selectedEmployee.id);
    } catch (error) {
      console.error('Error removing custom permission:', error);
      alert('حدث خطأ أثناء إزالة التخصيص');
    }
  };

  const resetForm = () => {
    setSelectedPermissionId('');
    setReason('');
    setExpiresAt('');
    setHasExpiry(false);
  };

  const getAvailablePermissions = () => {
    if (!employeePermissions) return permissions;
    
    const currentPermissionCodes = employeePermissions.permissions.map(p => p.permission_code);
    
    return permissions.map(category => ({
      ...category,
      permissions: category.permissions.filter(p => 
        !currentPermissionCodes.includes(p.code)
      )
    })).filter(category => category.permissions.length > 0);
  };

  const getRevokeablePermissions = () => {
    if (!employeePermissions) return [];
    
    return permissions.map(category => ({
      ...category,
      permissions: category.permissions.filter(p => 
        employeePermissions.permissions.some(ep => ep.permission_code === p.code)
      )
    })).filter(category => category.permissions.length > 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            تخصيص الصلاحيات الفردية
          </h1>
          <p className="text-gray-600">
            منح أو إلغاء صلاحيات محددة لموظفين معينين
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Employees List */}
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">قائمة الموظفين</h2>
            
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {employees.length === 0 ? (
                <p className="text-gray-500 text-center py-4">لا يوجد موظفين</p>
              ) : (
                employees.map((emp) => (
                  <button
                    key={emp.id}
                    onClick={() => handleEmployeeSelect(emp)}
                    className={`w-full text-right p-4 rounded-lg border-2 transition-all ${
                      selectedEmployee?.id === emp.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 bg-white'
                    }`}
                  >
                    <div className="font-bold text-gray-900">{emp.full_name_ar}</div>
                    <div className="text-sm text-gray-500">{emp.position}</div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Custom Permissions Manager */}
          <div className="col-span-12 md:col-span-8 bg-white rounded-lg shadow-sm p-6">
            {!selectedEmployee ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="mt-4 text-gray-500">اختر موظفاً لتخصيص صلاحياته</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Employee Info */}
                <div className="border-b border-gray-200 pb-4">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedEmployee.full_name_ar}</h2>
                  <p className="text-gray-600">{selectedEmployee.position} - {selectedEmployee.department}</p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowGrantModal(true)}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    + منح صلاحية إضافية
                  </button>
                  <button
                    onClick={() => setShowRevokeModal(true)}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                  >
                    - إلغاء صلاحية موجودة
                  </button>
                </div>

                {/* Current Custom Permissions */}
                {customPermissions && (customPermissions.custom_grants.length > 0 || customPermissions.custom_revokes.length > 0) && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">الصلاحيات المخصصة الحالية</h3>
                    
                    {/* Granted Permissions */}
                    {customPermissions.custom_grants.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-green-600 mb-2">
                          صلاحيات ممنوحة ({customPermissions.custom_grants.length}):
                        </p>
                        <div className="space-y-2">
                          {customPermissions.custom_grants.map((grant, index) => (
                            <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3 flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{grant.permission.name_ar}</p>
                                <p className="text-xs text-gray-500 mt-1">{grant.permission.code}</p>
                                {grant.reason && (
                                  <p className="text-sm text-gray-600 mt-1">السبب: {grant.reason}</p>
                                )}
                                {grant.expires_at && (
                                  <p className="text-xs text-orange-600 mt-1">
                                    تنتهي في: {new Date(grant.expires_at).toLocaleDateString('ar-SA')}
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={() => handleRemoveCustomPermission(grant.permission.id)}
                                className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                              >
                                إزالة
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Revoked Permissions */}
                    {customPermissions.custom_revokes.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-red-600 mb-2">
                          صلاحيات ملغاة ({customPermissions.custom_revokes.length}):
                        </p>
                        <div className="space-y-2">
                          {customPermissions.custom_revokes.map((revoke, index) => (
                            <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3 flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{revoke.permission.name_ar}</p>
                                <p className="text-xs text-gray-500 mt-1">{revoke.permission.code}</p>
                                {revoke.reason && (
                                  <p className="text-sm text-gray-600 mt-1">السبب: {revoke.reason}</p>
                                )}
                              </div>
                              <button
                                onClick={() => handleRemoveCustomPermission(revoke.permission.id)}
                                className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                              >
                                إزالة
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* No Custom Permissions */}
                {customPermissions && customPermissions.custom_grants.length === 0 && customPermissions.custom_revokes.length === 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                    <p className="text-gray-600">لا يوجد تخصيصات فردية لهذا الموظف</p>
                    <p className="text-sm text-gray-500 mt-1">صلاحياته تأتي من الأدوار المُسندة فقط</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Grant Permission Modal */}
        {showGrantModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-4">منح صلاحية إضافية</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اختر الصلاحية *
                  </label>
                  <select
                    value={selectedPermissionId}
                    onChange={(e) => setSelectedPermissionId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">-- اختر صلاحية --</option>
                    {getAvailablePermissions().map((category) => (
                      <optgroup key={category.category.code} label={category.category.name_ar}>
                        {category.permissions.map((perm) => (
                          <option key={perm.id} value={perm.id}>
                            {perm.name_ar} ({perm.code})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    سبب منح الصلاحية *
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="اكتب سبب منح هذه الصلاحية..."
                  />
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={hasExpiry}
                      onChange={(e) => setHasExpiry(e.target.checked)}
                      className="h-4 w-4 text-green-600 rounded"
                    />
                    <span className="mr-2 text-sm text-gray-700">صلاحية مؤقتة (تحديد تاريخ انتهاء)</span>
                  </label>
                </div>

                {hasExpiry && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      تاريخ الانتهاء
                    </label>
                    <input
                      type="datetime-local"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleGrantPermission}
                  disabled={!selectedPermissionId || !reason || saving}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'جاري المنح...' : 'منح الصلاحية'}
                </button>
                <button
                  onClick={() => {
                    setShowGrantModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revoke Permission Modal */}
        {showRevokeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-4">إلغاء صلاحية موجودة</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اختر الصلاحية المراد إلغاؤها *
                  </label>
                  <select
                    value={selectedPermissionId}
                    onChange={(e) => setSelectedPermissionId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">-- اختر صلاحية --</option>
                    {getRevokeablePermissions().map((category) => (
                      <optgroup key={category.category.code} label={category.category.name_ar}>
                        {category.permissions.map((perm) => (
                          <option key={perm.id} value={perm.id}>
                            {perm.name_ar} ({perm.code})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    سبب إلغاء الصلاحية *
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="اكتب سبب إلغاء هذه الصلاحية..."
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleRevokePermission}
                  disabled={!selectedPermissionId || !reason || saving}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {saving ? 'جاري الإلغاء...' : 'إلغاء الصلاحية'}
                </button>
                <button
                  onClick={() => {
                    setShowRevokeModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeCustomPermissionsPage;
